import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { noop, filter, tap, EMPTY, catchError, take, switchMap } from 'rxjs';
import { TsSnackbarService, TsTableService } from 'tsui';

import clone from 'lodash-es/clone';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { AccountService } from 'app/core';
import { RelatedToType } from 'app/shared/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import { FeatureFlag, NewPermission } from '@core/enums';
import { PropertyTenantsService } from '../tenants.service';
import { Property } from 'app/modules/projects/projects.types';
import { FieldDef, SavedView } from 'app/core/models/account.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { PropertyUnit } from 'app/modules/projects/details/tenants/tenants.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { getAllowedFields, removeArchivedFieldsFromTableDefinition } from 'app/shared/functions';
import { PropertyTenantsAddTenantComponent } from '../modals/add-tenant/add-tenant.component';
import { PropertyTenantsRentScheduleComponent } from '../modals/rent-schedule/rent-schedule.component';
import { PropertyTenantsRentEscalationComponent } from '../modals/rent-escalation/rent-escalation.component';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { TableStatusRenderer } from '@shared/components/ts-table-renderers/table-status-renderer/table-status-renderer.component';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { GridReadyEvent } from 'ag-grid-enterprise';

@Component({
  selector: 'property-tenants-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyTenantsDetailsComponent implements OnInit, OnDestroy {
  savedViews: SavedView[];
  savedView: SavedView;
  propertyUnit: PropertyUnit;
  propertyTenantsDataSource: MatTableDataSource<any>;
  displayColumns: string[] = [];
  filteredFieldDefs: FieldDef[] = [];
  facilityReportsDataSource: MatTableDataSource<any>;
  data: Data;
  isLoading: boolean = false;
  drawerMode: 'over' | 'side';
  NewPermission = NewPermission;

  private property: Property;
  private fieldDefs: FieldDef[] = [];
  private facilityReportFieldDefs: FieldDef[] = [];
  private _fieldsDefsPanelTemplatePortal: TemplatePortal;
  private _fieldDefsPanelOverlayRef: OverlayRef;

  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;
  @ViewChild('matMenu', { static: true }) matMenu: MatMenu;
  @ViewChild('fieldDefsPanel', { static: false }) private _fieldDefsPanel: TemplateRef<any>;
  @ViewChild('fieldDefsPanelOrigin', { static: false }) private _fieldDefsPanelOrigin: MatButton;
  @ViewChild('fileInput', { static: false }) fileInput: any;

  private readonly destroyRef = inject(DestroyRef);
  columnDefs: ColumnDef[] = [];
  tsActions: TableActions[] = [];

  gridOptions = {
    animateRows: true,
    suppressAggAtRootLevel: true,
    autoSizeStrategy: {
      type: 'fitCellContents',
      defaultMinWidth: 150,
    },
    loading: false,
  };

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly propertyTenantsService: PropertyTenantsService,
    private readonly propertiesService: PropertiesService,
    private readonly matDialog: MatDialog,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly overlay: Overlay,
    private readonly _matDialog: MatDialog,
    private readonly store: Store,
    private readonly snackBar: TsSnackbarService,
  ) {
    this.propertyTenantsDataSource = new MatTableDataSource();
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      this.data = data;
      this.fieldDefs = getAllowedFields(data.fieldDefs, RelatedToType.PropertyTenant);
      this.facilityReportFieldDefs = getAllowedFields(data.fieldDefs, RelatedToType.FacilityReport);
    });

    this.fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.PropertyTenant, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
    this.filteredFieldDefs = this.fieldDefs;

    this.propertiesService.property$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((property: Property) => {
      this.property = property;
    });

    this.propertyTenantsService.propertyUnit$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((propertyUnit) => !!propertyUnit),
      )
      .subscribe({
        next: (propertyUnit: PropertyUnit) => {
          this.propertyUnit = propertyUnit;

          if (this.propertyUnit.property_tenants === null) {
            this.propertyUnit.property_tenants = [];
            this.propertyTenantsDataSource = new MatTableDataSource();
            this.propertyTenantsDataSource.data = this.propertyUnit.property_tenants;
          }

          if (this.propertyUnit.facility_reports === null) {
            this.propertyUnit.facility_reports = [];
            this.facilityReportsDataSource = new MatTableDataSource();
            this.facilityReportsDataSource.data = this.propertyUnit.facility_reports;
          }

          if (this.savedView) {
            this.renderSavedView();
          }

          this.changeDetectorRef.markForCheck();
        },
        error: (error) => {
          console.error('Error in propertyUnit$ subscription:', error);
          this.snackBar.open(`Error fetching related data. Please refresh the browser or try again later.`, 'error');
        },
      });

    this.accountService.savedViews$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((savedViews: SavedView[]) => {
      if (savedViews) {
        this.savedViews = savedViews
          .filter(
            (ov) =>
              ov.related_to_type === RelatedToType.PropertyTenant ||
              ov.related_to_type === RelatedToType.FacilityReport,
          )
          .reverse();
        let id = UrlUtils.getFromLocalStorage('propertyTenantSavedView', false);
        if (id) {
          const foundViews = this.savedViews.filter((sv) => sv.id == id);
          if (foundViews) {
            this.savedView = foundViews[0];
          } else {
            this.savedView = this.savedViews[0];
          }
        } else {
          this.savedView = this.savedViews[0];
        }
        this.renderSavedView();
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this._fieldDefsPanelOverlayRef) {
      this._fieldDefsPanelOverlayRef.dispose();
    }
  }

  private getColDefs(): void {
    this.columnDefs = this.displayColumns.map((column) => this.lookupField(column));
    this.setColumnsAttributes();
  }

  //TS TABLE
  private setColumnsAttributes() {
    this.columnDefs.map((fieldDef, index) => {
      if (fieldDef) {
        fieldDef.tsTableOptions = {
          sortable: true,
          resizable: true,
          editable: !fieldDef.meta?.readonly,
          ...fieldDef.tsTableOptions,
        };

        if (fieldDef.name === 'workflow_state') {
          fieldDef.tsTableOptions.cellRenderer = TableStatusRenderer;
          fieldDef.tsTableOptions.cellRendererParams = {
            statusColors: {
              draft: '#6B7280',
              submitted: '#3F83F8',
              rejected: '#F05252',
              accepted: '#0E9F6E',
            },
          };
        }
      }
    });
    this.setTableActions();
  }

  setTableActions(): void {
    this.tsActions = [
      {
        label: 'Rent Escalations',
        action: (item) => this.showRentEscalation(item),
      },
      {
        label: 'Rent Schedule',
        action: (item) => this.showRentSchedule(item),
      },
      {
        label: 'Delete',
        action: (item) => this.deleteLease(item),
      },
    ];
  }

  onOpenDetails(item: any): void {
    this.router.navigateByUrl(
      `/space/void?return=/space/leases/${this.savedView.id}/${item.id}&backUrl=${this.router.url}`,
      { skipLocationChange: true },
    );
  }

  private renderSavedView(): void {
    if (this.savedView.related_to_type === RelatedToType.PropertyTenant) this.renderPropertyTenantSavedView();
    if (this.savedView.related_to_type === RelatedToType.FacilityReport) this.renderFacilityReportsSavedView();
  }

  private renderFacilityReportsSavedView(): void {
    this.filteredFieldDefs = this.facilityReportFieldDefs;
    this.displayColumns = removeArchivedFieldsFromTableDefinition(
      JSON.parse(this.savedView.definition).table.fields,
      this.facilityReportFieldDefs,
    );
    this.cleanseDisplayColumns();
    this.facilityReportsDataSource = new MatTableDataSource();
    this.facilityReportsDataSource.data = this.propertyUnit.facility_reports;
  }

  private renderPropertyTenantSavedView(): void {
    this.filteredFieldDefs = this.fieldDefs;
    this.displayColumns = removeArchivedFieldsFromTableDefinition(
      JSON.parse(this.savedView.definition).table.fields,
      this.fieldDefs,
    );
    this.displayColumns.push('drawer');
    this.cleanseDisplayColumns();

    const savedFilter = this.savedView?.saved_filter;

    this.propertyTenantsDataSource = new MatTableDataSource();
    let data = this.propertyUnit.property_tenants;

    if (savedFilter) {
      let filter = JSON.parse(savedFilter.filters);

      if (filter && filter['fjson']) {
        let fjson = JSON.parse(filter['fjson']);
        if (fjson) {
          for (let idx in fjson) {
            let customFilter = fjson[idx];
            data = data.filter((ov) => {
              if (customFilter[1] == 'eq') {
                return ov[customFilter[0]] == customFilter[2];
              } else if (customFilter[1] == 'ne') {
                return ov[customFilter[0]] != customFilter[2];
              }
            });
          }
        }
      }
    }
    this.propertyTenantsDataSource.data = data;
  }

  private loadPropertyUnit(): void {
    this.propertyTenantsService
      .getPropertyUnitById(this.propertyUnit.id.toString())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }

  private saveColumns(): void {
    this.savedView.definition = JSON.stringify({ table: { fields: this.displayColumns } });
    this.accountService.updateSavedView(this.savedView).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
  }

  private cleanseDisplayColumns(): void {
    this.displayColumns = clone(this.displayColumns).filter((x) => x !== 'drawer');
    this.getColDefs();
  }

  private removeDisplayColumn(fieldName: string): void {
    this.displayColumns.splice(
      this.displayColumns.findIndex((item) => item === fieldName),
      1,
    );

    this.cleanseDisplayColumns();
    this.saveColumns();
    this.changeDetectorRef.markForCheck();
  }

  private addDisplayColumn(fieldName: string): void {
    let index = this.displayColumns.findIndex((item) => item === fieldName);
    if (index == -1) {
      this.displayColumns.unshift(fieldName);
      this.cleanseDisplayColumns();
      this.saveColumns();
    }

    this.changeDetectorRef.markForCheck();
  }

  private moveToFirst(columnName: string, columnsList: FieldDef[] = []): FieldDef[] {
    const index = columnsList.findIndex((column) => column?.name === columnName);
    if (index !== -1) {
      const coverImage = columnsList.splice(index, 1);
      columnsList.unshift(coverImage.shift());
    }
    return columnsList;
  }

  private closeDisplayFieldsPanel(): void {
    if (this._fieldDefsPanelOverlayRef && this._fieldDefsPanelOverlayRef.hasAttached()) {
      this._fieldDefsPanelOverlayRef.detach();
      this.filteredFieldDefs = this.fieldDefs;
    }

    if (this._fieldsDefsPanelTemplatePortal && this._fieldsDefsPanelTemplatePortal.isAttached) {
      this._fieldsDefsPanelTemplatePortal.detach();
    }
  }

  addLease(): void {
    this._matDialog
      .open(PropertyTenantsAddTenantComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { property: this.property, propertyUnit: this.propertyUnit },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }

  addFacilityReport(): void {
    const facilityReport = {
      property_unit_id: this.propertyUnit.id,
    };
    this.propertyTenantsService
      .createFacilityReport(this.propertyUnit.id, facilityReport)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }

  onColumnMoved(event: any): void {
    TsTableService.moveColumns(event, this.displayColumns, [], (columns: string[]) => {
      this.displayColumns = columns;
      this.saveColumns();
    });
  }

  dropColumns($event: CdkDragDrop<any[]>): void {
    const from: number = $event.previousIndex;
    const to: number = $event.currentIndex;

    if (to !== from) {
      moveItemInArray(this.displayColumns, from, to);
      this.saveColumns();
    }
  }

  showRentEscalation(tenant): void {
    this.matDialog
      .open(PropertyTenantsRentEscalationComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { propertyUnit: this.propertyUnit, property: this.property, propertyTenant: tenant },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadPropertyUnit();
      });
  }

  showRentSchedule(tenant): void {
    this.matDialog
      .open(PropertyTenantsRentScheduleComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { propertyUnit: this.propertyUnit, property: this.property, propertyTenant: tenant },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadPropertyUnit();
      });
  }

  deleteLease(tenant): void {
    const unitId = this.propertyUnit.id;
    this.propertyTenantsService
      .deletePropertyTenant(unitId, tenant.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.propertyTenantsDataSource.data = this.propertyTenantsDataSource.data.filter((t) => t.id !== tenant.id);
          this.changeDetectorRef.markForCheck();
        }),
        catchError((error) => {
          console.error('Error deleting lease: ', error);
          this.snackBar.open('Error deleting lease. Please try again later.', 'error');
          return EMPTY;
        }),
      )
      .subscribe(noop);
  }

  deleteFacilityReport(report) {
    const unitId = this.propertyUnit.id;
    this.propertyTenantsService
      .deleteFacilityReport(unitId, report.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }

  deletePropertyUnit(): void {
    const id = this.propertyUnit.id;

    // Get the current list of property units before deletion
    this.propertyTenantsService.propertyUnits$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        switchMap((currentUnits: PropertyUnit[]) => {
          return this.propertyTenantsService.deletePropertyUnit(id).pipe(
            tap((deletedUnit: PropertyUnit) => {
              if (deletedUnit) {
                // Store the current units and find the index of the unit to be deleted
                const units = currentUnits || [];
                const currentIndex = units.findIndex((unit) => unit.id === deletedUnit.id);
                // Find the next available unit
                const nextUnit = units[currentIndex + 1] || units[currentIndex - 1];

                // Navigate to the next unit if available, otherwise go back to the list
                if (nextUnit) {
                  this.router.navigate(['../', nextUnit.id], { relativeTo: this.activatedRoute });
                } else {
                  this.router.navigate(['../'], { relativeTo: this.activatedRoute });
                }
              }
            }),
          );
        }),
      )
      .subscribe({
        error: (error) => {
          console.error('Error deleting property unit:', error);
          this.snackBar.open('Error deleting property unit. Please try again later.', 'error');
        },
        complete: () => {
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  toggleDisplayColumn(fieldName: string, change: MatCheckboxChange): void {
    if (change.checked) {
      this.addDisplayColumn(fieldName);
    } else {
      this.removeDisplayColumn(fieldName);
    }
  }

  onCellEditingStopped($event) {
    const editedData = $event.data;
    this.onUpdate(editedData);
  }

  onUpdate(params): void {
    const options = { ...params, id: params?.id };
    const selectColumn = [];
    selectColumn.push(Object.keys(params)[0]);

    if (params[Object.keys(params)[0]] !== 'add_new') {
      if (this.savedView.related_to_type === RelatedToType.PropertyTenant) {
        this.propertyTenantsService
          .updatePropertyTenant(this.propertyUnit.id, options, selectColumn.join(','))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(noop);
      }
      if (this.savedView.related_to_type === RelatedToType.FacilityReport) {
        this.propertyTenantsService
          .updateFacilityReport(this.propertyUnit.id, options, selectColumn.join(','))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(noop);
      }
    } else {
      this.propertyTenantsService
        .getPropertyUnitById(this.propertyUnit.id.toString())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(noop);
    }
  }

  filterFields(event): void {
    const value = event.target.value.toLowerCase();
    this.filteredFieldDefs = this.fieldDefs.filter((fieldDef) => fieldDef.label.toLowerCase().includes(value));
  }

  filterFieldsInputKeyDown(event): void {
    if (event.key !== 'Enter') {
      return;
    }

    const fieldDef = this.filteredFieldDefs[0];
    const isFieldVisible = this.fieldDefs.find((item) => item.name === fieldDef.name);

    if (isFieldVisible) {
      this.removeDisplayColumn(fieldDef.name);
    } else {
      this.addDisplayColumn(fieldDef.name);
    }
  }

  openFieldDefsPanel(): void {
    for (let column of this.displayColumns?.slice()?.reverse()) {
      this.fieldDefs = this.moveToFirst(column, this.fieldDefs);
    }
    this._fieldDefsPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this._fieldDefsPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    this._fieldDefsPanelOverlayRef
      .attachments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._fieldDefsPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    this._fieldsDefsPanelTemplatePortal = new TemplatePortal(this._fieldDefsPanel, this.viewContainerRef);
    this._fieldDefsPanelOverlayRef.attach(this._fieldsDefsPanelTemplatePortal);

    this._fieldDefsPanelOverlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeDisplayFieldsPanel();
      });
  }

  lookupField(name: string): FieldDef | null {
    let fields: any;
    if (this.savedView.related_to_type === RelatedToType.PropertyTenant)
      fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (this.savedView.related_to_type === RelatedToType.FacilityReport)
      fields = this.facilityReportFieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  onSavedViewSelectionChange(view: SavedView): void {
    this.savedView = view;
    this.renderSavedView();
    UrlUtils.saveToLocalStorage('propertyTenantSavedView', this.savedView.id, false);
  }

  canEdit(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.LEASES_EDIT));
    }

    return true;
  }

  canImportLeaseAbstract(): boolean {
    return (
      this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.POSTAL_LEASE_ABSTRACT)) && this.canEdit()
    );
  }

  onSortChanged(event: any): void {
    const definition = JSON.parse(this.savedView.definition);

    const sortState = event.api.getColumnState().filter((s) => s.sort !== null);

    if (sortState.length) {
      const [column] = sortState;
      const newActiveSort = { active: column.colId, direction: column.sort };
      definition.table.sort = newActiveSort;
    } else {
      delete definition.table.sort;
    }

    this.accountService
      .updateSavedView({ ...this.savedView, definition: JSON.stringify(definition) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }

  onGridReady(event: GridReadyEvent): void {
    if (this.savedView?.definition) {
      const definition = JSON.parse(this.savedView.definition);

      if (definition.table.sort) {
        const colId = definition.table.sort.active;
        const sort = definition.table.sort.direction;
        event.api.applyColumnState({ state: [{ colId, sort }], applyOrder: false });
      }
    }
  }

  onSelectLeaseAbstractFile(event: any): void {
    if (event.target.files.length) {
      const file = event.target.files.item(0);
      this.propertyTenantsService
        .importLeaseAbstract(this.property.id, this.propertyUnit.id, file)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response.errors?.length > 0) {
            this.snackBar.open(response.errors, 'error', null, { duration: 5000 });
          } else {
            this.snackBar.open('Lease abstract imported successfully', 'success');
            this.loadPropertyUnit();
          }
          // Reset the file input so the same file can be selected again
          if (this.fileInput && this.fileInput.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        });
    }
  }
}
